import { z } from "zod";

import { TASK_STATUS_VALUES } from "@/lib/tasks/status";

import type { RouteContract } from "./types";

const taskStatus = z.enum(TASK_STATUS_VALUES);

// Wire mirror of RunStory (lib/observability/run-story.ts) — the narrator's
// output is the activity contract; surfaces render it without re-deriving.
const runStory = z
  .object({
    runId: z.string(),
    runKind: z.string().nullable(),
    kindLabel: z.string(),
    status: z.enum(["pending", "running", "succeeded", "failed"]),
    tone: z.enum(["queued", "active", "done", "failed", "canceled"]),
    statusLabel: z.string(),
    statusVerb: z.string(),
    error: z.string().nullable(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    createdAt: z.string(),
    durationMs: z.number().nullable(),
    steps: z.array(
      z.object({
        id: z.string(),
        stage: z.string(),
        label: z.string(),
        message: z.string(),
        data: z.record(z.string(), z.unknown()),
        at: z.string(),
        elapsedMs: z.number().nullable(),
        text: z.string(),
      }),
    ),
  })
  .passthrough();

const taskSnapshot = z.object({
  task: z
    .object({
      id: z.string(),
      title: z.string(),
      description: z.string().nullable(),
      status: taskStatus,
      type: z.string(),
      origin: z.string(),
      startAt: z.string().nullable(),
      createdAt: z.string(),
      updatedAt: z.string(),
      accountId: z.string().nullable(),
      assigneeUserId: z.string().nullable(),
      chatId: z.string().nullable(),
      state: z.record(z.string(), z.unknown()),
      agentId: z.string().nullable(),
    })
    .passthrough(),
  activity: z.array(runStory),
});

export const createTaskRequest = z
  .object({
    title: z.unknown().optional(),
    status: z.unknown().optional(),
    accountId: z.unknown().optional(),
    startAt: z.unknown().optional(),
    description: z.unknown().optional(),
    agentId: z.unknown().optional(),
    askedValues: z.unknown().optional(),
  })
  .catch({});

export const createTaskResponse = z.object({ id: z.string() });

export const createTaskContract: RouteContract<
  typeof createTaskRequest,
  typeof createTaskResponse
> = {
  method: "POST",
  path: "/api/tasks",
  request: createTaskRequest,
  response: createTaskResponse,
};

export const getTaskContract: RouteContract<null, typeof taskSnapshot> = {
  method: "GET",
  path: (params) => `/api/tasks/${params.id}`,
  request: null,
  response: taskSnapshot,
};

export const patchTaskRequest = z
  .object({
    status: z.unknown().optional(),
    assigneeUserId: z.unknown().optional(),
    title: z.unknown().optional(),
    startAt: z.unknown().optional(),
    description: z.unknown().optional(),
    accountId: z.unknown().optional(),
    agentId: z.unknown().optional(),
  })
  .catch({});

export const patchTaskContract: RouteContract<
  typeof patchTaskRequest,
  typeof taskSnapshot
> = {
  method: "PATCH",
  path: (params) => `/api/tasks/${params.id}`,
  request: patchTaskRequest,
  response: taskSnapshot,
};

export const deleteTaskResponse = z.object({ ok: z.literal(true) });

export const deleteTaskContract: RouteContract<
  null,
  typeof deleteTaskResponse
> = {
  method: "DELETE",
  path: (params) => `/api/tasks/${params.id}`,
  request: null,
  response: deleteTaskResponse,
};

const suggestionOption = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  domain: z.string().nullable().optional(),
});

export const suggestTaskRequest = z
  .object({
    prompt: z.unknown().optional(),
    agents: z.array(suggestionOption).optional().catch(undefined),
    accounts: z.array(suggestionOption).optional().catch(undefined),
    now: z.unknown().optional(),
  })
  .catch({});

export const suggestTaskResponse = z.object({
  agentId: z.string().nullable(),
  accountId: z.string().nullable(),
  startAt: z.string().nullable(),
});

export const suggestTaskContract: RouteContract<
  typeof suggestTaskRequest,
  typeof suggestTaskResponse
> = {
  method: "POST",
  path: "/api/tasks/suggest",
  request: suggestTaskRequest,
  response: suggestTaskResponse,
};

export type TaskSnapshotResponse = z.infer<typeof taskSnapshot>;
